import React from 'react';
import { CreditCard, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getBankNameFromCardNumber } from '@/lib/bankCardBin';
import { cardToIbanApi } from '@/lib/api/cardToIban';
import { formatIranIbanDashed, isCompleteIranIban } from '@/lib/bsiTransferMode';

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/** قالب استاندارد کارت: ۴۴۴۴ ۴۴۴۴ ۴۴۴۴ ۴۴۴۴ (۱۶ رقم + ۳ فاصله = ۱۹ کاراکتر) */
function formatCardDisplay(digits: string): string {
  const d = digitsOnly(digits).slice(0, 16);
  const parts = d.match(/.{1,4}/g) ?? [];
  return parts.join(' ');
}

/** ورودی شبیه کارت است (نه شبا IR… و نه ۲۴ رقم شبا بدون IR). */
function looksLikeCardInput(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/^IR/i.test(trimmed.replace(/\s+/g, ''))) return false;
  if (!/^[\d\s]+$/.test(trimmed)) return false;
  const d = digitsOnly(trimmed);
  if (d.length === 0 || d.length > 19) return false;
  // ۲۴ رقم خالص = شبا بدون IR
  if (d.length === 24) return false;
  return true;
}

type Props = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  onHolderResolved?: (holder: { firstName: string; lastName: string }) => void;
  label?: string;
  hint?: string;
};

const BsiDestinationInput: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
  onHolderResolved,
  label = 'شبا / کارت مقصد',
  hint = 'شبا یا شماره کارت ۱۶ رقمی — کارت به‌صورت خودکار به شبا تبدیل می‌شود.',
}) => {
  const [converting, setConverting] = React.useState(false);
  const [status, setStatus] = React.useState<{ text: string; error?: boolean } | null>(null);
  const [bankName, setBankName] = React.useState<string | null>(null);
  const convertTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastConvertedCardRef = React.useRef<string | null>(null);

  const isCard = looksLikeCardInput(value);
  const cardDigits = digitsOnly(value);

  React.useEffect(() => {
    if (isCard && cardDigits.length >= 6) {
      setBankName(getBankNameFromCardNumber(cardDigits) ?? null);
    } else {
      setBankName(null);
    }
  }, [cardDigits, isCard]);

  const convertCard = React.useCallback(
    async (cardNumber: string) => {
      const digits = digitsOnly(cardNumber);
      if (digits.length !== 16) return;
      if (lastConvertedCardRef.current === digits) return;

      setConverting(true);
      setStatus({ text: 'در حال تبدیل کارت به شبا…' });
      try {
        const res = await cardToIbanApi.lookupFromCard(digits);
        const iban = res.data?.iban?.trim();
        if (!iban) {
          throw new Error(res.message || 'شبا از کارت استخراج نشد.');
        }
        const formatted = formatIranIbanDashed(iban);
        onChange(formatted);
        lastConvertedCardRef.current = digits;
        const holder = (res.data?.account_holder_name ?? '').trim();
        if (holder && onHolderResolved) {
          const parts = holder.split(/\s+/);
          onHolderResolved({
            firstName: parts[0] || '',
            lastName: parts.slice(1).join(' ') || '',
          });
        }
        setStatus({
          text: `کارت به شبا تبدیل شد${res.data?.bank_name ? ` · ${res.data.bank_name}` : ''}${holder ? ` · ${holder}` : ''}`,
        });
      } catch (e) {
        lastConvertedCardRef.current = null;
        setStatus({
          text: e instanceof Error ? e.message : 'تبدیل کارت ناموفق بود.',
          error: true,
        });
      } finally {
        setConverting(false);
      }
    },
    [onChange, onHolderResolved],
  );

  const handleChange = (raw: string) => {
    const trimmed = raw.trim();
    if (looksLikeCardInput(trimmed) || looksLikeCardInput(raw)) {
      const digits = digitsOnly(raw).slice(0, 16);
      onChange(formatCardDisplay(digits));
      setStatus(null);
      if (digits.length !== 16) {
        lastConvertedCardRef.current = null;
      }
      if (convertTimerRef.current) clearTimeout(convertTimerRef.current);
      if (digits.length === 16 && !disabled && !converting) {
        convertTimerRef.current = setTimeout(() => void convertCard(digits), 450);
      }
      return;
    }

    lastConvertedCardRef.current = null;
    onChange(formatIranIbanDashed(trimmed));
    if (trimmed && !isCompleteIranIban(trimmed)) {
      setStatus(null);
    }
  };

  React.useEffect(() => {
    return () => {
      if (convertTimerRef.current) clearTimeout(convertTimerRef.current);
    };
  }, []);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="relative">
        <Input
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="شبا یا شماره کارت ۱۶ رقمی"
          dir="ltr"
          className="font-mono pr-9"
          disabled={disabled || converting}
          inputMode="text"
          autoComplete="off"
          maxLength={isCard ? 19 : 33}
        />
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
          {converting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CreditCard className="h-4 w-4" />
          )}
        </div>
      </div>
      {bankName && isCard ? <p className="text-xs text-slate-500">{bankName}</p> : null}
      {status ? (
        <p className={`text-xs ${status.error ? 'text-rose-700' : 'text-emerald-700'}`}>{status.text}</p>
      ) : (
        <p className="text-[11px] text-slate-500">{hint}</p>
      )}
    </div>
  );
};

export default BsiDestinationInput;
